import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "./store";
import http from "app/requests/axiosInstance";

export interface ClientsSliceState {
    ccNumber:string;
    ccToken:string;
  id: number;
  name: string;
  email: string;
  key: string;
  phone: string;
  notes: any[];
  gender: string;
  avatar: string;
  archivedAt?: string;
  managers: any[];
  createdAt: string;
  updatedAt: string;
}

interface ClientsSliceReduxState {
  clients: ClientsSliceState[];
  meta: {
    totalPages: number;
    currentPage: number;
    perPage: number;
    totalCount: number;
  };
}

const initialState = {
  clients: [],
  meta: {
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
    totalCount: 0,
  },
} as ClientsSliceReduxState;

export const getClients = createAsyncThunk(
  "clients/getClients",
  async (
    {
      page = 1,
      perPage = 10,
      sortBy = "name",
      sortOrder = "asc",
      managerIds,
      search,
    }: {
      page?: number;
      perPage?: number;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      managerIds?: number[];
      search?: string;
    },
    thunkAPI
  ) => {
    let url = `/v1/clients?page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_order=${sortOrder}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    // if (managerIds) {
    //   url += `&manager_ids=${managerIds}`;
    // }

    try {
      const { data } = await http.get(url);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createClient = createAsyncThunk(
  "clients/createClient",
  async (
    {
      name,
      email,
      phone,
      gender,
      notes,
        dob,
        address,
    }: {
      name: string;
      email: string;
      phone: string;
      gender: string;
      notes: string;
      dob:string;
      address:string;
    },
    thunkAPI
  ) => {
    try {
      const { data } = await http.post("/v1/clients", {
        client: {
          name,
          email,
          phone,
          gender,
            dob,address,
          notes
        },
      });
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateClient = createAsyncThunk(
  "clients/updateClient",
  async (
    {
      id,
      name,
      email,
      phone,
      gender,
      notes,
      avatar,
        dob,address,
    }: {
      id: number;
      name?: string;
      email?: string;
      phone?: string;
      gender?: string;
      notes?: string;
      dob?:string;
      address?:string;
      avatar?: File | null; // Change to File or null
    },
    thunkAPI
  ) => {
    try {
      let data;

      if (avatar) {
        const formData = new FormData();
        formData.append("client[avatar]", avatar); // Attach the avatar file to the form data

        data = await http.patch(`/v1/clients/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // If there's no avatar, send the data as JSON
        const jsonPayload = {
          client: {
            name,
            email,
            phone,
            gender,
              dob,
              address,
            notes,
          },
        };

        data = await http.patch(`/v1/clients/${id}`, jsonPayload);
      }

      return data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const searchClients = createAsyncThunk(
  "clients/searchClients",
  async (searchQuery: string, thunkAPI) => {
    try {
      const { data } = await http.get(
        `/v1/clients?search=${encodeURIComponent(searchQuery)}&per_page=10`
      );
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// Searches all of the merchant's clients on the server without touching the
// redux list, so pickers (booking, quick checkout) don't clobber the Clients page.
export const fetchClientSearchResults = async (
  search: string,
  perPage = 25
): Promise<{ clients: ClientsSliceState[]; totalCount: number }> => {
  const { data } = await http.get(
    `/v1/clients?search=${encodeURIComponent(search)}&per_page=${perPage}&sort_by=name&sort_order=asc`
  );
  const clients: ClientsSliceState[] = data?.clients ?? [];
  return { clients, totalCount: data?.meta?.totalCount ?? clients.length };
};

// One page of clients for an infinitely-scrolling picker (empty search = all clients)
export const fetchClientPage = async (
  search: string,
  page: number,
  perPage = 25
): Promise<{ clients: ClientsSliceState[]; totalCount: number }> => {
  const { data } = await http.get(
    `/v1/clients?search=${encodeURIComponent(search)}&page=${page}&per_page=${perPage}&sort_by=name&sort_order=asc`
  );
  const clients: ClientsSliceState[] = data?.clients ?? [];
  return { clients, totalCount: data?.meta?.totalCount ?? clients.length };
};

// Shown under a client picker when the search matched more clients than were returned
export const clientSearchOverflowMessage = (shown: number, totalCount: number) =>
  totalCount > shown
    ? `Showing ${shown} of ${totalCount} — type a phone or email to narrow`
    : null;

export const archiveClient = createAsyncThunk(
  "clients/archiveClient",
  async (id: number, thunkAPI) => {
    try {
      const { data } = await http.post(`/v1/clients/${id}/archive`, {});
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const importClientsCSV = createAsyncThunk(
  "clients/importClientsCSV",
  async (formData: FormData, thunkAPI) => {
    try {
      const { data } = await http.post(
        "/v1/clients/import_csv_clients",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getImportCsvClientsJobStatus = createAsyncThunk(
  "clients/getImportCsvClientsJobStatus",
  async (importId: string, thunkAPI) => {
    try {
      const { data } = await http.get(
        `/v1/clients/import_csv_clients_job_status`,
        {
          params: {
            importId,
            snakeCase: true,
          },
        }
      );
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getClients.fulfilled, (state, action) => {
      const { clients: newClients, meta } = action.payload;

      // Directly set the new sales and meta information
      state.clients = newClients;
      state.meta = meta;
    });
    builder.addCase(createClient.fulfilled, (state, action) => {
      state.clients.unshift(action.payload);
    });
    builder.addCase(updateClient.fulfilled, (state, action) => {
      const index = state.clients.findIndex(
        (client) => client.id === action.payload.id
      );

      state.clients[index] = action.payload;
    });
    builder.addCase(archiveClient.fulfilled, (state, action) => {
      // remove client from state
      const before = state.clients.length;
      state.clients = state.clients.filter(
        (client) => client.id !== action.payload.id
      );
      // Keep the header count in step without a refetch
      if (state.clients.length < before) {
        state.meta.totalCount = Math.max(0, state.meta.totalCount - 1);
        state.meta.totalPages = state.meta.perPage > 0
          ? Math.ceil(state.meta.totalCount / state.meta.perPage)
          : state.meta.totalPages;
      }
    });
    builder.addCase(searchClients.fulfilled, (state, action) => {
      const { clients: newClients, meta } = action.payload;
      state.clients = newClients;
      state.meta = meta;
    });
  },
});

export const selectClients = (state: RootState) => state.clients;
